import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "../lib/api";
import { DEFAULT_STATI } from "../lib/statiAggiuntivi";
import { Input } from "./ui/input";
import { ChevronsUpDown } from "lucide-react";

// Combobox autocomplete predittivo per "Stato Aggiuntivo".
// La lista è renderizzata in un portale (position: fixed) così esce dalla scheda
// e non viene tagliata dai contenitori con overflow.
export default function StatoAggiuntivoField({ value, onChange, onCommit, testId }) {
  const [val, setVal] = useState(value || "");
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const ref = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => { setVal(value || ""); }, [value]);
  useEffect(() => {
    api.get("/studio/me").then(({ data }) => {
      const o = data?.settings?.stato_aggiuntivo_options;
      setOpts(Array.isArray(o) && o.length ? o : DEFAULT_STATI);
    }).catch(() => setOpts(DEFAULT_STATI));
  }, []);

  const updateCoords = useCallback(() => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < 240 && r.top > spaceBelow;
    setCoords({ left: r.left, width: r.width, top: openUp ? undefined : r.bottom + 4, bottom: openUp ? window.innerHeight - r.top + 4 : undefined });
  }, []);

  useLayoutEffect(() => { if (open) updateCoords(); }, [open, updateCoords]);
  useEffect(() => {
    if (!open) return;
    const onScrollResize = () => updateCoords();
    window.addEventListener("scroll", onScrollResize, true);
    window.addEventListener("resize", onScrollResize);
    return () => {
      window.removeEventListener("scroll", onScrollResize, true);
      window.removeEventListener("resize", onScrollResize);
    };
  }, [open, updateCoords]);

  useEffect(() => {
    const h = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const filtered = val && val.trim() ? opts.filter((o) => o.toLowerCase().includes(val.toLowerCase())) : opts;
  const change = (v) => { setVal(v); onChange && onChange(v); };
  const pick = (o) => { setVal(o); onChange && onChange(o); onCommit && onCommit(o); setOpen(false); };

  return (
    <div className="relative w-full" ref={ref}>
      <div className="relative">
        <Input value={val} onChange={(e) => change(e.target.value)} onFocus={() => setOpen(true)} onBlur={() => onCommit && onCommit(val)}
          data-testid={testId} className="h-9 bg-slate-900/60 border-slate-700 pr-8 w-full" placeholder="Seleziona o digita…" autoComplete="off" />
        <button type="button" tabIndex={-1} onMouseDown={(e) => { e.preventDefault(); setOpen((o) => !o); }} data-testid={`${testId}-toggle`}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"><ChevronsUpDown className="h-4 w-4" /></button>
      </div>
      {open && filtered.length > 0 && coords && createPortal(
        <div ref={panelRef} style={{ position: "fixed", left: coords.left, top: coords.top, bottom: coords.bottom, width: coords.width }}
          className="z-[9999] rounded-md border border-border bg-popover shadow-xl max-h-56 overflow-y-auto" data-testid={`${testId}-list`}>
          {filtered.map((o, i) => (
            <button key={i} type="button" onMouseDown={(e) => { e.preventDefault(); pick(o); }} data-testid={`${testId}-opt`}
              className="w-full text-left px-3 py-2 text-sm text-popover-foreground hover:bg-accent hover:text-accent-foreground border-b border-border last:border-0">{o}</button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}
